/**
 * Curated pool of 8 demo product photos used when a product has no
 * uploaded image. Picking the same photo for the same product-id keeps
 * the browse grid stable across renders, while the variety across
 * products lets the SmartImage skeleton show off graceful staggered
 * loading.
 *
 * Sources in priority order:
 *   1. Admin-uploaded pool via `GET /api/shop/sample-images` — sellers
 *      curate 8 photos that match their actual catalogue. Fetched once
 *      on module load and cached in a promise for the lifetime of the
 *      SPA (React Query is not available at module scope here).
 *   2. Shipped Picsum defaults — used for any slot the admin hasn't
 *      filled in yet, so the pool always has 8 usable images.
 */
import { useSyncExternalStore } from "react";

// Shipped defaults — deterministic, cacheable Picsum IDs.
const DEFAULT_IMAGE_IDS = [
  "96",   // camera / lens
  "175",  // headphones on wood
  "26",   // watch macro
  "180",  // book on table
  "1050", // shoes / sneakers
  "365",  // desk kit
  "43",   // laptop keys
  "1025", // dog with product
] as const;

export const SAMPLE_IMAGE_COUNT = DEFAULT_IMAGE_IDS.length;

// FNV-1a-ish rolling hash — small, fast, deterministic, and spreads
// short slugs across all 8 buckets evenly.
//
// IMPORTANT: use `Math.imul` for the multiply step. Vanilla `*` uses
// double-precision floats, and once `h * 16777619` exceeds 2^53 (which
// happens quickly on ~20-char slugs) the low bits get shredded before
// `>>> 0` gets a chance to truncate — this collapses the distribution
// so most products cluster into just 2-3 buckets. `Math.imul` performs
// true 32-bit signed integer multiplication and keeps the hash uniform.
function hashToBucket(seed: string, buckets: number): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h % buckets;
}

function defaultImage(seed: string, size: number): string {
  const id = DEFAULT_IMAGE_IDS[hashToBucket(seed || "socibaba", DEFAULT_IMAGE_IDS.length)];
  return `https://picsum.photos/id/${id}/${size}/${size}`;
}

// --- Admin pool fetching -------------------------------------------------
// The pool is fetched once on first `sampleImageFor` call, cached in a
// Promise so concurrent callers share the same in-flight request, and
// materialised into an array of (data_url | null) once resolved. Any
// null slot uses the shipped default so the pool always has 8 entries.
//
// A small `useSyncExternalStore`-friendly subscription surface lets React
// components re-render the moment the async fetch resolves — without
// this, /shop cards computed with `productImage()` would keep showing
// Picsum fallbacks on first paint until an unrelated re-render lands.
let poolCache: (string | null)[] | null = null;
let poolPromise: Promise<(string | null)[]> | null = null;
let poolVersion = 0;
const subscribers = new Set<() => void>();
const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function notifySubscribers(): void {
  poolVersion++;
  subscribers.forEach((cb) => { try { cb(); } catch { /* ignore */ } });
}

async function fetchPool(): Promise<(string | null)[]> {
  try {
    const resp = await fetch(`${BACKEND}/api/shop/sample-images`, { credentials: "omit" });
    if (!resp.ok) throw new Error(String(resp.status));
    const j = await resp.json();
    const slots = j?.slots || {};
    return Array.from({ length: SAMPLE_IMAGE_COUNT }, (_, i) => slots[`slot_${i}`] || null);
  } catch {
    return Array.from({ length: SAMPLE_IMAGE_COUNT }, () => null);
  }
}

function primePool(): void {
  if (poolCache || poolPromise) return;
  poolPromise = fetchPool().then((p) => {
    poolCache = p;
    notifySubscribers();
    // Broadcast so anything listening (e.g. React admin card) refreshes.
    try { window.dispatchEvent(new CustomEvent("socibaba:sample-pool-ready")); } catch { /* ignore */ }
    return p;
  });
}

/**
 * Force a re-fetch of the admin-uploaded pool. Called from the admin
 * card after a save so the next `sampleImageFor` uses the fresh photo.
 * Every subscribed React component re-renders automatically.
 */
export function refreshSampleImagePool(): void {
  poolCache = null;
  poolPromise = null;
  notifySubscribers();
  primePool();
}

/**
 * React hook — subscribes the calling component to sample-pool changes.
 * Use inside any card that computes `productImage()` from a product with
 * no image_url so it re-renders when the admin-uploaded pool arrives.
 */
export function useSamplePool(): number {
  return useSyncExternalStore(
    (cb) => {
      subscribers.add(cb);
      // Kick off the fetch on first subscribe if nothing else has yet.
      primePool();
      return () => { subscribers.delete(cb); };
    },
    () => poolVersion,
    () => 0,
  );
}

export function sampleImageFor(seed: string, size: number = 600): string {
  primePool();
  const idx = hashToBucket(seed || "socibaba", SAMPLE_IMAGE_COUNT);
  const uploaded = poolCache ? poolCache[idx] : null;
  if (uploaded) return uploaded; // Admin-uploaded data-URL wins.
  return defaultImage(seed, size);
}

/** Blur preview URL for the same image (tiny, quickly loadable). */
export function sampleBlurFor(seed: string): string {
  primePool();
  const idx = hashToBucket(seed || "socibaba", SAMPLE_IMAGE_COUNT);
  const uploaded = poolCache ? poolCache[idx] : null;
  if (uploaded) return uploaded; // Same data URL for uploaded photos.
  const id = DEFAULT_IMAGE_IDS[idx];
  return `https://picsum.photos/id/${id}/24/24?blur=4`;
}
